import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { UpgradeRefusalNotice } from '../../../shared/UpgradeRefusalNotice';
import { clearSubscriptionRestrictionNotice, upgradeRequiredFromError, type SubscriptionRestriction } from '../../../shared/subscriptionRestriction';
import type { DepartmentOption } from '../../people/types';
import { jobRoleGateway } from '../index';
import { CONCURRENCY_CONFLICT_MESSAGE, isConcurrencyConflict, isForbidden, isNotDraft, jobRoleErrorMessage } from '../jobRoleErrors';
import {
  contentFromForm, emptyForm, formFromRole, headerFromForm, validateForActivation, validateForDraft,
  type FormIssue, type RoleForm, type RoleFormKey,
} from '../roleForm';
import { inputFromPayBandForm, type PayBandForm as PayBandFormValues } from '../payBand';
import {
  ROLE_VS_ACCESS_EXPLAINER, emptyClassification, normalizeJobRoleContent,
  type JobRoleContent, type JobRoleDetail, type JobRoleTemplateDetail, type JobRoleTemplateListItem, type OccupationItem, type OccupationSystem, type PayBand,
} from '../types';
import { useJobRoleCapabilities } from '../useCapabilities';
import { ErrorSummary } from './ErrorSummary';
import { AddDepartmentDialog } from './guided/AddDepartmentDialog';
import type { GroupedComboboxHandle } from './guided/GroupedCombobox';
import { ModalDialog } from './ModalDialog';
import { CompensationStep, type PendingBand } from './wizard/CompensationStep';
import { DetailsStep } from './wizard/DetailsStep';
import { QualificationsStep } from './wizard/QualificationsStep';
import { ResponsibilitiesStep } from './wizard/ResponsibilitiesStep';
import { ReviewStep } from './wizard/ReviewStep';
import { StartStep } from './wizard/StartStep';
import { STEP_LABEL, stepOfField, type StepKey } from './wizard/steps';
import { WizardContext, type WizardApi } from './wizard/WizardContext';

export type WizardMode = 'blank' | 'template' | 'edit';
export type WizardOutcome = 'draft-saved' | 'activated';

export interface RoleWizardDialogProps {
  mode: WizardMode;
  /** Required for `edit`: the role whose open Draft is being edited. */
  roleId?: string;
  departments: DepartmentOption[];
  onClose: () => void;
  /** Called after the role was saved (and activated when asked). The parent reloads and says what happened. `note` is a follow-up the
   * person should read (for example the optional pay band could not be saved), shown beside the parent's own message. */
  onDone: (role: JobRoleDetail, outcome: WizardOutcome, note?: string) => void;
  /** The role was saved from inside the dialog (Compensation step) while the dialog stays open: the parent reloads its list. */
  onChanged?: () => void;
  /** A write came back 403: the parent switches the page to read-only. */
  onForbidden?: () => void;
  /** A department was created from inside the dialog: the parent adds it to its own list (it is already saved for the company). */
  onDepartmentCreated?: (department: DepartmentOption) => void;
}

interface Loaded {
  detail: JobRoleDetail;
  /** The content being edited: the pending Draft's (`draftContent`), which for an Active role with a new draft is not the detail's own (in-force) content. */
  content: JobRoleContent;
}

async function loadEditable(roleId: string, signal: AbortSignal): Promise<Loaded> {
  const detail = await jobRoleGateway.getRole(roleId, signal);
  return { detail, content: normalizeJobRoleContent(detail.draftContent ?? detail.content) };
}

/** The template a role was made from: the source of the suggestions beside its fields. */
interface SuggestionTemplate { id: string; title: string; content: JobRoleContent }

/**
 * Add Role / Use EPN Template / Edit draft: one guided dialog. Start (template or blank), role details, responsibilities, qualifications,
 * compensation (optional) and a Review with Save as Draft or Activate. Every field comes with guidance from the field registry; suggestions are only
 * ever inserted by an explicit click and never overwrite what the person wrote; a department can be added without leaving the dialog. Nothing here sets
 * pay or access: a job role never grants a permission. The server is the only enforcer, so its refusals (duplicate title, stale version, not a Draft)
 * are shown as they come.
 */
export function RoleWizardDialog({ mode, roleId, departments, onClose, onDone, onChanged, onForbidden, onDepartmentCreated }: RoleWizardDialogProps) {
  const uid = useId();
  const editing = mode === 'edit';
  const { caps } = useJobRoleCapabilities();
  const [step, setStep] = useState<StepKey>(editing ? 'details' : 'start');
  const [form, setForm] = useState<RoleForm>(emptyForm);
  const [baseContent, setBaseContent] = useState<JobRoleContent | undefined>(undefined);
  const [startMode, setStartMode] = useState<'template' | 'blank'>(mode === 'template' ? 'template' : 'blank');
  const [templates, setTemplates] = useState<JobRoleTemplateListItem[] | null>(null);
  const [templatesError, setTemplatesError] = useState<string | null>(null);
  // The template library needs Plus. When the server refuses with SUBSCRIPTION_UPGRADE_REQUIRED the person is told why and can carry on from a
  // blank role; a company's OWN roles are never gated, and nothing they typed is lost.
  const [planRefusal, setPlanRefusal] = useState<SubscriptionRestriction | null>(null);
  const [templatesLocked, setTemplatesLocked] = useState(false);
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [pendingTemplate, setPendingTemplate] = useState<JobRoleTemplateDetail | null>(null);
  const [appliedTemplate, setAppliedTemplate] = useState<SuggestionTemplate | null>(null);
  const [templateBusy, setTemplateBusy] = useState(false);
  const [replaceConfirm, setReplaceConfirm] = useState(false);
  const [initialJson, setInitialJson] = useState<string>(() => JSON.stringify(emptyForm()));
  const [loadState, setLoadState] = useState<'loading' | 'ready' | 'error'>(editing ? 'loading' : 'ready');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [issues, setIssues] = useState<FormIssue[]>([]);
  const [templateError, setTemplateError] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);
  const [busy, setBusy] = useState<'draft' | 'activate' | null>(null);
  const [status, setStatus] = useState('');
  const saved = useRef<{ id: string; concurrencyVersion: number } | null>(null);
  const concurrency = useRef<number | null>(null);
  // Compensation step (optional). The role id exists only after the first save, so a pay band entered before that is KEPT here and
  // sent right after the role is saved (setPayBand), never lost and never required. The benchmark view needs the saved role.
  const [savedId, setSavedId] = useState<string | null>(null);
  const [pendingBand, setPendingBand] = useState<PendingBand | null>(null);
  const pendingBandRef = useRef<PendingBand | null>(null);
  const [bandFormOpen, setBandFormOpen] = useState(false);
  const [existingBand, setExistingBand] = useState<PayBand | null | undefined>(undefined);
  const [benchRefresh, setBenchRefresh] = useState(0);
  // Departments: the ones the page passed, plus any added from inside this dialog (already saved for the company).
  const [createdDepartments, setCreatedDepartments] = useState<DepartmentOption[]>([]);
  const [departmentDialog, setDepartmentDialog] = useState<{ name: string } | null>(null);
  // Departments created from inside this dialog, so Review can mark them as new.
  const [newDepartmentIds, setNewDepartmentIds] = useState<string[]>([]);
  const departmentRef = useRef<GroupedComboboxHandle>(null);
  const [occupation, setOccupationState] = useState<Partial<Record<OccupationSystem, OccupationItem | null>>>({});
  const knownRoleId = editing ? (roleId ?? null) : savedId;
  const summaryRef = useRef<HTMLDivElement>(null);
  const serverErrorRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const firstStepRender = useRef(true);

  /** If `err` is the server's plan refusal for the template library, shows the explanation (and the locked-feature dialog) and returns true. */
  const refuseByPlan = useCallback((err: unknown): boolean => {
    const refusal = upgradeRequiredFromError(err);
    if (!refusal) return false;
    clearSubscriptionRestrictionNotice();
    setPlanRefusal(refusal);
    return true;
  }, []);

  const steps: StepKey[] = editing
    ? ['details', 'responsibilities', 'qualifications', 'compensation', 'review']
    : ['start', 'details', 'responsibilities', 'qualifications', 'compensation', 'review'];
  const stepIndex = steps.indexOf(step);

  const allDepartments = useMemo(
    () => [...departments, ...createdDepartments.filter((c) => !departments.some((d) => d.id === c.id))],
    [departments, createdDepartments],
  );

  // ---- loading ----

  const loadForEdit = useCallback((signal: AbortSignal) => {
    if (!roleId) return;
    setLoadState('loading');
    loadEditable(roleId, signal)
      .then(({ detail, content }) => {
        if (signal.aborted) return;
        if (!detail.hasDraft) {
          setLoadError('This role has no open draft to edit. Use Create New Version first.');
          setLoadState('error');
          return;
        }
        const next = formFromRole(detail, content);
        setForm(next);
        setInitialJson(JSON.stringify(next));
        setBaseContent(content);
        concurrency.current = detail.concurrencyVersion;
        setConflict(false);
        setServerError(null);
        setLoadState('ready');
        // The template this role came from, if any, is the source of suggestions beside its fields (never applied on its own).
        if (detail.sourceTemplateId) {
          jobRoleGateway
            .getTemplate(detail.sourceTemplateId, signal)
            .then((t) => { if (!signal.aborted) setAppliedTemplate({ id: t.id, title: t.title, content: normalizeJobRoleContent(t.content) }); })
            .catch(() => undefined);
        }
      })
      .catch((err: unknown) => {
        if (signal.aborted) return;
        if (isForbidden(err)) onForbidden?.();
        setLoadError(jobRoleErrorMessage(err, 'Could not load this role.'));
        setLoadState('error');
      });
  }, [roleId, onForbidden]);

  useEffect(() => {
    if (!editing) return;
    const controller = new AbortController();
    // eslint-disable-next-line react-hooks/set-state-in-effect
    loadForEdit(controller.signal);
    return () => controller.abort();
  }, [editing, loadForEdit]);

  useEffect(() => {
    if (editing || step !== 'start' || startMode !== 'template' || templates !== null) return;
    const controller = new AbortController();
    jobRoleGateway
      .listTemplates(undefined, controller.signal)
      .then((list) => { if (!controller.signal.aborted) setTemplates(list); })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        if (refuseByPlan(err)) {
          setTemplates([]);
          setTemplatesLocked(true);
          return;
        }
        if (isForbidden(err)) onForbidden?.();
        setTemplatesError(jobRoleErrorMessage(err, 'Could not load the EPN templates.'));
      });
    return () => controller.abort();
  }, [editing, step, startMode, templates, onForbidden, refuseByPlan]);

  // Focus moves to the step heading when the step changes (not on first render: the dialog itself takes focus then).
  useEffect(() => {
    if (firstStepRender.current) {
      firstStepRender.current = false;
      return;
    }
    headingRef.current?.focus();
  }, [step]);

  useEffect(() => {
    if (issues.length > 0) summaryRef.current?.focus();
  }, [issues]);

  useEffect(() => {
    if (serverError) serverErrorRef.current?.focus();
  }, [serverError]);

  // The Compensation step shows the role's current pay band when the role already exists (editing, or saved earlier in this dialog).
  useEffect(() => {
    if (step !== 'compensation' || !knownRoleId) return;
    const controller = new AbortController();
    jobRoleGateway
      .getPayBand(knownRoleId, controller.signal)
      .then((band) => { if (!controller.signal.aborted) setExistingBand(band); })
      .catch(() => { if (!controller.signal.aborted) setExistingBand(null); });
    return () => controller.abort();
  }, [step, knownRoleId]);

  // ---- form helpers ----

  const set = useCallback((key: RoleFormKey, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setIssues((prev) => (prev.some((i) => i.key === key) ? prev.filter((i) => i.key !== key) : prev));
  }, []);

  const setMany = useCallback((patch: Partial<RoleForm>) => {
    setForm((prev) => ({ ...prev, ...patch }));
    setIssues((prev) => prev.filter((i) => !(i.key in patch)));
  }, []);

  const errorOf = (key: string) => issues.find((i) => i.key === key)?.message;

  function focusField(key: string) {
    const wanted = stepOfField(key);
    if (wanted !== step) setStep(wanted);
    // Wait for the step to render before moving focus (a second try covers fields that mount a moment later).
    const focus = () => bodyRef.current?.querySelector<HTMLElement>(`[data-field="${key}"]`)?.focus();
    window.setTimeout(focus, 0);
    window.setTimeout(() => { if (!bodyRef.current?.contains(document.activeElement) || (document.activeElement as HTMLElement | null)?.dataset.field !== key) focus(); }, 120);
  }

  // ---- template choice: a template is only SHOWN on Step 1; its content is copied when the person chooses Next ----

  function applyTemplateDetail(detail: JobRoleTemplateDetail) {
    const suggestedDepartment = allDepartments.find((d) => d.isActive && d.name.toLowerCase() === (detail.suggestedDepartmentName ?? '').toLowerCase());
    const next = formFromRole(
      { title: detail.title, jobFamily: detail.jobFamily, careerLevel: detail.careerLevel, socCode: detail.socCode, onetCode: detail.onetCode, departmentId: suggestedDepartment?.id ?? null },
      detail.content,
    );
    setForm(next);
    setInitialJson(JSON.stringify(next));
    setBaseContent(normalizeJobRoleContent(detail.content));
    setAppliedTemplate({ id: detail.id, title: detail.title, content: normalizeJobRoleContent(detail.content) });
    setIssues([]);
    setStatus(`The ${detail.title} template was copied into your role. Everything in it can be changed.`);
  }

  async function chooseTemplate(t: JobRoleTemplateListItem) {
    setTemplateBusy(true);
    setServerError(null);
    setTemplateError(null);
    setStatus(`Loading the ${t.title} template…`);
    try {
      const detail = await jobRoleGateway.getTemplate(t.id);
      setPendingTemplate(detail);
      setTemplateId(t.id);
      setStatus(`The ${t.title} template is selected. It will be copied into your own role when you choose Next.`);
    } catch (err) {
      if (refuseByPlan(err)) {
        setStatus('');
        return;
      }
      if (isForbidden(err)) onForbidden?.();
      setServerError(jobRoleErrorMessage(err, 'Could not load that template.'));
      setStatus('');
    } finally {
      setTemplateBusy(false);
    }
  }

  function clearTemplate() {
    setPendingTemplate(null);
    setTemplateId(null);
    setStatus('');
  }

  function switchStartMode(next: 'template' | 'blank') {
    setStartMode(next);
    setIssues([]);
    setTemplateError(null);
    setReplaceConfirm(false);
    if (next === 'blank') setPlanRefusal(null);
    if (next === 'blank' && (templateId || appliedTemplate)) {
      const dirty = JSON.stringify(form) !== initialJson;
      setPendingTemplate(null);
      setTemplateId(null);
      setAppliedTemplate(null);
      if (dirty) {
        // What the person wrote is theirs: it stays. Only the link to the template is removed.
        setStatus('The template was removed from this role. What you have written is kept.');
      } else {
        const blank = emptyForm();
        setForm(blank);
        setInitialJson(JSON.stringify(blank));
        setBaseContent(undefined);
        setStatus('');
      }
    }
  }

  // ---- navigation ----

  function advance() {
    setServerError(null);
    setStep(steps[Math.min(steps.length - 1, stepIndex + 1)]!);
  }

  function goNext() {
    if (step === 'start') {
      if (startMode === 'template' && !templateId) {
        setTemplateError('Choose a template, or switch to a blank role.');
        window.setTimeout(() => bodyRef.current?.querySelector<HTMLElement>('[data-field="template"]')?.focus(), 0);
        return;
      }
      if (startMode === 'template' && pendingTemplate && appliedTemplate?.id !== pendingTemplate.id) {
        if (JSON.stringify(form) !== initialJson) {
          setReplaceConfirm(true); // the person already wrote something: ask before the template text replaces it
          return;
        }
        applyTemplateDetail(pendingTemplate);
      }
      advance();
      return;
    }
    const found = step === 'details' ? validateForDraft(form) : [];
    setIssues(found);
    if (found.length > 0) return;
    advance();
  }

  function goBack() {
    setIssues([]);
    setServerError(null);
    setStep(steps[Math.max(0, stepIndex - 1)]!);
  }

  // ---- saving ----

  /** Sends the pay band kept from the Compensation step, now that the role exists. A failure never undoes the saved role: it comes back as a
   * sentence for the person to read, and the band can be set later from the role's Compensation tab. */
  async function applyPendingBand(id: string): Promise<string | null> {
    const pending = pendingBandRef.current;
    if (!pending) return null;
    pendingBandRef.current = null;
    setPendingBand(null);
    try {
      const band = await jobRoleGateway.setPayBand(id, inputFromPayBandForm(pending.values, pending.expected));
      setExistingBand(band);
      return null;
    } catch (err) {
      const why = isForbidden(err)
        ? 'you do not have permission to set pay bands'
        : isConcurrencyConflict(err)
          ? 'the pay band was changed by someone else in the meantime'
          : jobRoleErrorMessage(err, 'it could not be saved');
      return `The role was saved, but the pay band was not set: ${why}. You can set it from the role's Compensation tab.`;
    }
  }

  async function persist(activate: boolean, stay = false) {
    const found = activate ? validateForActivation(form) : validateForDraft(form);
    setIssues(found);
    if (found.length > 0) {
      const firstStep = stepOfField(found[0]!.key);
      if (firstStep !== step && step !== 'review') setStep(firstStep);
      return;
    }
    setBusy(activate ? 'activate' : 'draft');
    setServerError(null);
    setStatus(activate ? 'Activating…' : 'Saving the draft…');
    const header = headerFromForm(form);
    const content = contentFromForm(form, baseContent);
    try {
      let detail: JobRoleDetail;
      const known = saved.current;
      if (editing || known) {
        const id = editing ? roleId! : known!.id;
        const version = editing && !known ? concurrency.current! : known!.concurrencyVersion;
        detail = await jobRoleGateway.updateRole(id, { ...header, content, expectedConcurrencyVersion: version });
      } else if (templateId) {
        detail = await jobRoleGateway.createRoleFromTemplate(templateId, { title: header.title, roleCode: header.roleCode ?? null, departmentId: header.departmentId ?? null });
        saved.current = { id: detail.id, concurrencyVersion: detail.concurrencyVersion };
        // The template copy is the starting point; anything the person changed after choosing it is applied to their own Draft.
        if (JSON.stringify(form) !== initialJson || appliedTemplate?.id !== templateId) {
          detail = await jobRoleGateway.updateRole(detail.id, { ...header, content, expectedConcurrencyVersion: detail.concurrencyVersion });
        }
      } else {
        detail = await jobRoleGateway.createRole({ ...header, content });
      }
      saved.current = { id: detail.id, concurrencyVersion: detail.concurrencyVersion };
      setSavedId(detail.id);
      const bandNote = await applyPendingBand(detail.id);
      if (activate) {
        try {
          detail = await jobRoleGateway.activateRole(detail.id, detail.concurrencyVersion);
          saved.current = { id: detail.id, concurrencyVersion: detail.concurrencyVersion };
        } catch (err) {
          if (isForbidden(err)) onForbidden?.();
          setServerError(
            (isConcurrencyConflict(err)
              ? CONCURRENCY_CONFLICT_MESSAGE
              : `The role was saved as a draft, but it could not be activated. ${jobRoleErrorMessage(err, 'Please try again.')}`)
            + (bandNote ? ` ${bandNote}` : ''),
          );
          setConflict(isConcurrencyConflict(err));
          setStatus('');
          return;
        }
      }
      if (stay) {
        // Saved from the Compensation step: the dialog stays open so the benchmark for the new role can be looked at.
        setStatus('Draft saved.');
        setBenchRefresh((n) => n + 1);
        if (bandNote) setServerError(bandNote);
        onChanged?.();
        return;
      }
      setStatus(activate ? 'The role is active.' : 'Draft saved.');
      onDone(detail, activate ? 'activated' : 'draft-saved', bandNote ?? undefined);
    } catch (err) {
      if (refuseByPlan(err)) {
        setStatus('');
        return;
      }
      if (isForbidden(err)) onForbidden?.();
      if (isConcurrencyConflict(err)) setConflict(true);
      else if (isNotDraft(err)) setServerError('This role no longer has an open draft, so it cannot be edited. Reload it, or use Create New Version.');
      else setServerError(jobRoleErrorMessage(err, 'Could not save this role.'));
      setStatus('');
    } finally {
      setBusy(null);
    }
  }

  function reloadLatest() {
    if (!editing) return;
    setConflict(false);
    saved.current = null;
    const controller = new AbortController();
    loadForEdit(controller.signal);
  }

  // ---- departments ----

  function departmentCreated(department: DepartmentOption) {
    setCreatedDepartments((prev) => (prev.some((d) => d.id === department.id) ? prev : [...prev, department]));
    setNewDepartmentIds((prev) => (prev.includes(department.id) ? prev : [...prev, department.id]));
    set('departmentId', department.id);
    setDepartmentDialog(null);
    onDepartmentCreated?.(department);
    setStatus(`${department.name} was added as a department and selected.`);
    window.setTimeout(() => departmentRef.current?.focus(), 0);
  }

  function departmentCancelled() {
    setDepartmentDialog(null);
    window.setTimeout(() => departmentRef.current?.focus(), 0);
  }

  // ---- the context the steps read ----

  const api: WizardApi = {
    uid, form, set, setMany, errorOf, disabled: busy !== null, caps, editing, roleId: knownRoleId,
    startMode, templates, templatesError, templatesLocked, templateId, pendingTemplate, templateBusy, templateError,
    switchStartMode, chooseTemplate: (t) => void chooseTemplate(t), clearTemplate,
    suggestionTemplate: appliedTemplate,
    departments: allDepartments, newDepartmentIds, requestAddDepartment: (name) => setDepartmentDialog({ name }), departmentRef,
    occupation, setOccupation: (system, item) => setOccupationState((prev) => (prev[system] === item ? prev : { ...prev, [system]: item })),
  };

  // ---- render ----

  const title = editing ? 'Edit role draft' : mode === 'template' ? 'Use an EPN template' : 'Add role';
  const summaryIssues = issues.map((i) => ({ key: i.key as string, message: i.message }));
  const activationIssues = validateForActivation(form);
  const draftId = `${uid}-draft-explained`;
  const activateId = `${uid}-activate-explained`;
  const activateBlocked = activationIssues.length > 0;

  const footer = (
    <>
      <button type="button" className="jr-btn jr-btn--secondary" onClick={onClose} disabled={busy !== null}>Cancel</button>
      <span className="jr-modal__spacer" />
      {stepIndex > 0 && (
        <button type="button" className="jr-btn jr-btn--secondary" onClick={goBack} disabled={busy !== null}>Back</button>
      )}
      {step !== 'start' && step !== 'review' && loadState === 'ready' && (
        <button type="button" className="jr-btn jr-btn--secondary" onClick={() => void persist(false)} disabled={busy !== null}>
          {busy === 'draft' ? 'Saving…' : 'Save as Draft'}
        </button>
      )}
      {step !== 'review' && loadState === 'ready' && (
        <button type="button" className="jr-btn jr-btn--primary" onClick={goNext} disabled={busy !== null || templateBusy}>Next</button>
      )}
      {step === 'review' && (
        <>
          <button type="button" className="jr-btn jr-btn--secondary" aria-describedby={draftId} onClick={() => void persist(false)} disabled={busy !== null}>
            {busy === 'draft' ? 'Saving…' : 'Save as Draft'}
          </button>
          {/* aria-disabled, not disabled: it stays focusable so its reason can be read, and pressing it lists what is missing. */}
          <button
            type="button" className={`jr-btn jr-btn--primary${activateBlocked ? ' jr-btn--blocked' : ''}`} aria-describedby={activateId}
            aria-disabled={activateBlocked ? true : undefined} onClick={() => void persist(true)} disabled={busy !== null}
          >
            {busy === 'activate' ? 'Activating…' : 'Activate'}
          </button>
        </>
      )}
    </>
  );

  return (
    <WizardContext.Provider value={api}>
      <ModalDialog title={title} size="xl" busy={busy !== null} onClose={onClose} footer={loadState === 'ready' ? footer : (
        <>
          <button type="button" className="jr-btn jr-btn--secondary" onClick={onClose}>Close</button>
        </>
      )}>
        <div ref={bodyRef} className="jr-wizard">
          <p className="muted" id={`${uid}-explainer`}>{ROLE_VS_ACCESS_EXPLAINER}</p>
          {loadState === 'ready' && (
            <ol className="jr-steps" aria-label="Steps">
              {steps.map((s, i) => (
                <li key={s} aria-current={s === step ? 'step' : undefined} className={s === step ? 'jr-steps__item jr-steps__item--current' : i < stepIndex ? 'jr-steps__item jr-steps__item--done' : 'jr-steps__item'}>
                  <span className="jr-steps__n" aria-hidden="true">{i + 1}</span> {STEP_LABEL[s]}
                  {i < stepIndex && <span className="jr-sr-only"> (completed)</span>}
                </li>
              ))}
            </ol>
          )}
          <p className="jr-sr-only" role="status" aria-live="polite">{status}</p>

          {loadState === 'loading' && <p className="muted" role="status">Loading the draft{'…'}</p>}
          {loadState === 'error' && <p className="jr-error" role="alert">{loadError}</p>}

          {loadState === 'ready' && (
            <>
              <ErrorSummary ref={summaryRef} issues={summaryIssues} onSelect={focusField} />
              {planRefusal && (
                <UpgradeRefusalNotice
                  restriction={planRefusal}
                  fallbackFeatureName="Job role library"
                  nothingChanged="Nothing was created from the template, and what you entered is kept."
                >
                  <div className="jr-actions">
                    <button type="button" className="jr-btn jr-btn--secondary jr-btn--small" onClick={() => { setPlanRefusal(null); switchStartMode('blank'); }}>
                      Start from a blank role instead
                    </button>
                  </div>
                </UpgradeRefusalNotice>
              )}
              {serverError && (
                <div ref={serverErrorRef} tabIndex={-1} className="jr-error jr-error--block" role="alert" data-testid="save-error">
                  <p className="jr-error__title"><span aria-hidden="true">{'⚠'} </span>{step === 'review' ? 'The role was not saved as you asked' : 'Something went wrong'}</p>
                  <p>{serverError}</p>
                </div>
              )}
              {conflict && (
                <div className="jr-conflict" role="alert">
                  <p>{CONCURRENCY_CONFLICT_MESSAGE}</p>
                  {editing && <button type="button" className="jr-btn jr-btn--secondary" onClick={reloadLatest}>Reload latest version</button>}
                </div>
              )}

              <h4 ref={headingRef} tabIndex={-1} data-autofocus className="jr-wizard__heading">
                Step {stepIndex + 1} of {steps.length}: {STEP_LABEL[step]}
              </h4>

              {replaceConfirm && pendingTemplate && (
                <div className="jr-inline-confirm" role="group" aria-label="Replace what you wrote?" data-testid="replace-confirm">
                  <p><strong>Replace what you wrote?</strong> You have already written part of this role. The {pendingTemplate.title} template can replace it with its own text, or you can keep what you wrote and use the template only as a source of suggestions.</p>
                  <div className="jr-actions">
                    <button type="button" className="jr-btn jr-btn--primary jr-btn--small" autoFocus onClick={() => { setReplaceConfirm(false); setAppliedTemplate({ id: pendingTemplate.id, title: pendingTemplate.title, content: normalizeJobRoleContent(pendingTemplate.content) }); advance(); }}>
                      Keep mine
                    </button>
                    <button type="button" className="jr-btn jr-btn--secondary jr-btn--small" onClick={() => { setReplaceConfirm(false); applyTemplateDetail(pendingTemplate); advance(); }}>
                      Replace
                    </button>
                  </div>
                </div>
              )}

              {step === 'start' && <StartStep />}
              {step === 'details' && <DetailsStep />}
              {step === 'responsibilities' && <ResponsibilitiesStep />}
              {step === 'qualifications' && <QualificationsStep />}
              {step === 'compensation' && (
                <CompensationStep
                  existingBand={existingBand}
                  pendingBand={pendingBand}
                  bandFormOpen={bandFormOpen}
                  setBandFormOpen={setBandFormOpen}
                  onKeepBand={(values: PayBandFormValues) => {
                    const next = { values, expected: existingBand?.concurrencyVersion };
                    pendingBandRef.current = next;
                    setPendingBand(next);
                    setBandFormOpen(false);
                  }}
                  onRemoveBand={() => { pendingBandRef.current = null; setPendingBand(null); }}
                  knownRoleId={knownRoleId}
                  benchRefresh={benchRefresh}
                  busy={busy !== null}
                  onSaveForBenchmarks={() => void persist(false, true)}
                />
              )}
              {step === 'review' && (
                <ReviewStep
                  content={contentFromForm(form, baseContent)}
                  activationIssues={activationIssues}
                  savedClassification={baseContent?.classification ?? emptyClassification()}
                  pendingBand={pendingBand}
                  existingBand={existingBand}
                  onEdit={(s) => { setIssues([]); setStep(s); }}
                  onJump={focusField}
                  ids={{ draft: draftId, activate: activateId }}
                />
              )}
            </>
          )}
        </div>
      </ModalDialog>
      {departmentDialog && <AddDepartmentDialog initialName={departmentDialog.name} onClose={departmentCancelled} onCreated={departmentCreated} />}
    </WizardContext.Provider>
  );
}
